import React, { useState } from 'react';
import {
  Bell, Home, Briefcase, MessageSquare, User, Star, CheckCircle,
  ChevronLeft, Plus, MapPin, Search, Calendar, Send, Shield,
  TrendingUp, Users, Signal, Wifi, BatteryFull,
} from 'lucide-react';

/* ───────────────────────── Phone shell ───────────────────────── */
const Phone = ({ children, wrapClass = '', floatClass = 'hsx-float-a', delay = 0, testid }) => (
  <div className={wrapClass} data-testid={testid}>
    <div className={`hsx-phone ${floatClass}`} style={{ animationDelay: `${delay}s` }}>
      <div className="hsx-phone-notch" />
      <div className="hsx-phone-screen">{children}</div>
    </div>
  </div>
);

/* ───────────────────────── Shared bits ───────────────────────── */
const StatusBar = () => (
  <div className="flex items-center justify-between px-4 pt-6 pb-1.5">
    <span className="text-[8px] font-semibold text-white">9:41</span>
    <div className="flex items-center gap-1">
      <img src="/favicon-192x192.png?v=20260609" alt="HopSeat" className="h-3.5 w-3.5 object-contain" />
      <span className="text-[10px] font-extrabold leading-none tracking-tight text-white">Hop<span className="text-orange-400">Seat</span></span>
    </div>
    <div className="flex items-center gap-[3px] text-white/85">
      <Signal className="h-2.5 w-2.5" />
      <Wifi className="h-2.5 w-2.5" />
      <BatteryFull className="h-3 w-3" />
    </div>
  </div>
);

const StatTile = ({ label, value, accent }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.04] p-2">
    <p className="text-[8px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`mt-0.5 hsx-mono text-base font-bold leading-none ${accent ? 'text-orange-400' : 'text-white'}`}>{value}</p>
  </div>
);

const Pill = ({ children, tone = 'orange' }) => {
  const tones = {
    orange: 'bg-orange-500/15 text-orange-300',
    emerald: 'bg-emerald-500/15 text-emerald-300',
    slate: 'bg-white/10 text-slate-300',
  };
  return <span className={`rounded-full px-1.5 py-0.5 text-[7px] font-semibold ${tones[tone]}`}>{children}</span>;
};

const BottomNav = ({ active = 'home' }) => (
  <div className="mt-auto flex items-center justify-around border-t border-white/10 bg-[#0a0e17] px-4 py-2.5">
    <Home className={`h-4 w-4 ${active === 'home' ? 'text-orange-400' : 'text-slate-500'}`} />
    <Briefcase className={`h-4 w-4 ${active === 'gigs' ? 'text-orange-400' : 'text-slate-500'}`} />
    <MessageSquare className={`h-4 w-4 ${active === 'chat' ? 'text-orange-400' : 'text-slate-500'}`} />
    <User className={`h-4 w-4 ${active === 'profile' ? 'text-orange-400' : 'text-slate-500'}`} />
  </div>
);

const ScreenShell = ({ children }) => (
  <div className="flex h-full flex-col bg-[#0a0e17] text-white">{children}</div>
);

/* ═══════════════════════ COMPANY SCREENS ═══════════════════════ */

const CompanyHomeScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex items-center justify-between px-4 py-2">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-[9px] font-bold text-white">AC</div>
        <div>
          <p className="text-[8px] leading-none text-slate-400">Welcome back</p>
          <p className="text-[11px] font-bold leading-tight">Acme Coachways</p>
        </div>
      </div>
      <div className="relative">
        <Bell className="h-4 w-4 text-slate-400" />
        <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-orange-500" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2 px-4 pt-1">
      <StatTile label="Active Gigs" value="4" />
      <StatTile label="Applicants" value="12" accent />
      <StatTile label="Drivers Hired" value="28" />
      <StatTile label="Open Roles" value="3" accent />
    </div>
    <div className="mx-4 mt-3 rounded-lg border border-white/10 bg-white/[0.04] p-2.5">
      <p className="mb-1.5 text-[8px] uppercase tracking-wide text-slate-400">Bookings this week</p>
      <div className="flex h-12 items-end gap-1">
        {[40, 66, 52, 80, 58, 92, 72].map((h, i) => (
          <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-orange-500/40 to-orange-400" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
    <div className="mx-4 mt-3">
      <p className="mb-1.5 text-[8px] uppercase tracking-wide text-slate-400">Recent gigs</p>
      <div className="space-y-1.5">
        {[{ r: 'Nashville → Atlanta', s: 'Filled', t: 'emerald' }, { r: 'Austin → Dallas', s: 'Open', t: 'orange' }].map((x) => (
          <div key={x.r} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5">
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3 text-orange-400" />
              <span className="text-[9px] font-medium">{x.r}</span>
            </div>
            <Pill tone={x.t}>{x.s}</Pill>
          </div>
        ))}
      </div>
    </div>
    <BottomNav active="home" />
  </ScreenShell>
);

const ApplicantsScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex items-center gap-2 px-4 py-2">
      <ChevronLeft className="h-4 w-4 text-slate-400" />
      <p className="text-[11px] font-bold">Gig Applicants</p>
    </div>
    <div className="mx-4 rounded-xl border border-orange-400/20 bg-orange-500/10 p-2.5">
      <p className="text-[10px] font-bold">Nashville → Atlanta</p>
      <p className="mt-0.5 text-[8px] text-slate-300">2 buses · <span className="text-orange-300">$450/day</span> · Jun 12–18</p>
    </div>
    <p className="mx-4 mt-3 text-[8px] uppercase tracking-wide text-slate-400">12 Applicants</p>
    <div className="mx-4 mt-1.5 space-y-2">
      {[{ n: 'Marcus Hale', r: '4.9', i: 'MH' }, { n: 'Dana Whitfield', r: '4.8', i: 'DW' }, { n: 'Carlos Reyes', r: '5.0', i: 'CR' }].map((d) => (
        <div key={d.n} className="rounded-lg border border-white/10 bg-white/[0.04] p-2">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-700 text-[8px] font-bold">{d.i}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <p className="text-[9px] font-semibold">{d.n}</p>
                <Star className="h-2.5 w-2.5 fill-orange-400 text-orange-400" />
                <span className="text-[8px] text-slate-400">{d.r}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-[7px] text-emerald-300">
                <CheckCircle className="h-2.5 w-2.5" /> CDL · FMCSA verified
              </div>
            </div>
            <button className="rounded-md bg-orange-500 px-2 py-1 text-[8px] font-semibold text-white">Accept</button>
          </div>
        </div>
      ))}
    </div>
    <BottomNav active="gigs" />
  </ScreenShell>
);

const MyGigsScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex items-center justify-between px-4 py-2">
      <p className="text-[11px] font-bold">My Gigs</p>
      <button className="flex items-center gap-1 rounded-full bg-orange-500 px-2 py-1 text-[8px] font-semibold text-white">
        <Plus className="h-3 w-3" /> Post
      </button>
    </div>
    <div className="mx-4 mt-1 space-y-2">
      {[{ r: 'Nashville → Atlanta', m: '2 buses · $450/day', s: 'Open', t: 'orange' },
        { r: 'LA → Las Vegas', m: '1 bus · $520/day', s: 'Filled', t: 'emerald' },
        { r: 'Denver → Salt Lake', m: '3 buses · $510/day', s: 'Open', t: 'orange' },
        { r: 'Seattle → Portland', m: '1 bus · $430/day', s: 'Draft', t: 'slate' }].map((x) => (
        <div key={x.r} className="rounded-lg border border-white/10 bg-white/[0.04] p-2.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-semibold">{x.r}</p>
            <Pill tone={x.t}>{x.s}</Pill>
          </div>
          <p className="mt-0.5 text-[8px] text-slate-400">{x.m}</p>
        </div>
      ))}
    </div>
    <div className="mx-4 mt-3 rounded-xl border border-dashed border-orange-400/30 bg-orange-500/[0.06] p-2.5 text-center">
      <p className="text-[9px] font-semibold text-orange-300">+ Post a new gig</p>
      <p className="mt-0.5 text-[7px] text-slate-400">Reach verified CDL drivers in minutes</p>
    </div>
    <BottomNav active="gigs" />
  </ScreenShell>
);

const RosterScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex items-center gap-2 px-4 py-2">
      <Users className="h-4 w-4 text-orange-400" />
      <p className="text-[11px] font-bold">Driver Roster</p>
    </div>
    <div className="mx-4 mt-1 space-y-2">
      {[{ n: 'Marcus Hale', s: 'On a gig', t: 'emerald', i: 'MH' },
        { n: 'Dana Whitfield', s: 'Available', t: 'orange', i: 'DW' },
        { n: 'Carlos Reyes', s: 'Available', t: 'orange', i: 'CR' },
        { n: 'Tasha Brooks', s: 'Off duty', t: 'slate', i: 'TB' },
        { n: 'Leo Park', s: 'On a gig', t: 'emerald', i: 'LP' }].map((d) => (
        <div key={d.n} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.04] p-2">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-700 text-[8px] font-bold">{d.i}</div>
            <div>
              <p className="text-[9px] font-semibold">{d.n}</p>
              <div className="mt-0.5 flex items-center gap-1 text-[7px] text-emerald-300"><Shield className="h-2.5 w-2.5" /> Verified</div>
            </div>
          </div>
          <Pill tone={d.t}>{d.s}</Pill>
        </div>
      ))}
    </div>
    <BottomNav active="profile" />
  </ScreenShell>
);

const RevenueScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex items-center gap-2 px-4 py-2">
      <TrendingUp className="h-4 w-4 text-emerald-400" />
      <p className="text-[11px] font-bold">Revenue</p>
    </div>
    <div className="grid grid-cols-2 gap-2 px-4 pt-1">
      <StatTile label="MTD" value="$18.4K" accent />
      <StatTile label="Pending" value="$4.2K" />
      <StatTile label="Gigs filled" value="31" />
      <StatTile label="Avg / gig" value="$486" accent />
    </div>
    <div className="mx-4 mt-3 rounded-lg border border-white/10 bg-white/[0.04] p-2.5">
      <p className="mb-1.5 text-[8px] uppercase tracking-wide text-slate-400">Last 8 weeks</p>
      <div className="flex h-16 items-end gap-1">
        {[34, 48, 40, 62, 55, 74, 68, 90].map((h, i) => (
          <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-emerald-500/30 to-emerald-400" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
    <div className="mx-4 mt-3 flex items-center justify-between rounded-lg border border-emerald-400/20 bg-emerald-500/[0.07] px-2.5 py-2">
      <span className="text-[8px] text-slate-300">Up vs last month</span>
      <span className="hsx-mono text-[10px] font-bold text-emerald-300">+12%</span>
    </div>
    <BottomNav active="home" />
  </ScreenShell>
);

/* ═══════════════════════ DRIVER SCREENS ═══════════════════════ */

const DriverHomeScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex items-center justify-between px-4 py-2">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-[9px] font-bold text-white">MH</div>
        <div>
          <p className="text-[8px] leading-none text-slate-400">On the board</p>
          <p className="text-[11px] font-bold leading-tight">Marcus Hale</p>
        </div>
      </div>
      <div className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[7px] font-semibold text-emerald-300">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Available
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2 px-4 pt-1">
      <StatTile label="Match score" value="94" accent />
      <StatTile label="New gigs" value="18" />
      <StatTile label="Applied" value="3" accent />
      <StatTile label="Rating" value="4.9" />
    </div>
    <p className="mx-4 mt-3 text-[8px] uppercase tracking-wide text-slate-400">Top matches for you</p>
    <div className="mx-4 mt-1.5 space-y-2">
      {[{ r: 'Nashville → Atlanta', p: '$450/day', m: '94% match' },
        { r: 'Austin → Dallas', p: '$420/day', m: '88% match' },
        { r: 'Denver → Salt Lake', p: '$510/day', m: '85% match' }].map((g) => (
        <div key={g.r} className="rounded-lg border border-white/10 bg-white/[0.04] p-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3 text-orange-400" />
              <span className="text-[9px] font-semibold">{g.r}</span>
            </div>
            <Pill tone="emerald">{g.m}</Pill>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="hsx-mono text-[9px] text-orange-300">{g.p}</span>
            <button className="rounded-md bg-orange-500 px-2 py-0.5 text-[7px] font-semibold text-white">Apply</button>
          </div>
        </div>
      ))}
    </div>
    <BottomNav active="home" />
  </ScreenShell>
);

const DriverBayScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex items-center justify-between px-4 py-2">
      <p className="text-[11px] font-bold">The Driver Bay</p>
      <Search className="h-3.5 w-3.5 text-slate-400" />
    </div>
    <div className="flex gap-1.5 px-4 pb-1">
      {['All', 'For sale', 'Wanted', 'Gear'].map((t, i) => (
        <span key={t} className={`rounded-full px-2 py-0.5 text-[7px] font-semibold ${i === 0 ? 'bg-orange-500 text-white' : 'bg-white/10 text-slate-300'}`}>{t}</span>
      ))}
    </div>
    <div className="grid grid-cols-2 gap-2 px-4 pt-1.5">
      {[{ t: '2016 Prevost H3-45', p: '$310K', c: 'For sale' },
        { t: 'Lead driver wanted', p: 'Tour', c: 'Wanted' },
        { t: 'Bay parking — Nash', p: '$25/nt', c: 'For rent' },
        { t: 'Spare alternator', p: '$180', c: 'Gear' }].map((x) => (
        <div key={x.t} className="rounded-lg border border-white/10 bg-white/[0.04] p-2">
          <div className="mb-1.5 h-10 rounded-md bg-gradient-to-br from-slate-700/60 to-slate-800/60" />
          <p className="text-[8px] font-semibold leading-tight">{x.t}</p>
          <div className="mt-1 flex items-center justify-between">
            <span className="hsx-mono text-[8px] text-orange-300">{x.p}</span>
            <span className="text-[6px] text-slate-400">{x.c}</span>
          </div>
        </div>
      ))}
    </div>
    <BottomNav active="gigs" />
  </ScreenShell>
);

const MessagesScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2">
      <ChevronLeft className="h-4 w-4 text-slate-400" />
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-500 text-[8px] font-bold">AC</div>
      <div>
        <p className="text-[10px] font-bold leading-tight">Acme Coachways</p>
        <p className="text-[7px] text-emerald-300">online</p>
      </div>
    </div>
    <div className="flex flex-1 flex-col gap-2 px-3 py-3">
      <div className="max-w-[78%] self-start rounded-2xl rounded-tl-sm bg-white/[0.06] px-2.5 py-1.5 text-[8px] text-slate-100">Hey Marcus — you free for the Atlanta run Jun 12–18?</div>
      <div className="max-w-[78%] self-end rounded-2xl rounded-tr-sm bg-orange-500 px-2.5 py-1.5 text-[8px] text-white">Yes! I&apos;m verified and available. Pay rate?</div>
      <div className="max-w-[78%] self-start rounded-2xl rounded-tl-sm bg-white/[0.06] px-2.5 py-1.5 text-[8px] text-slate-100">$450/day + per diem. Sending the offer now.</div>
      <div className="max-w-[78%] self-end rounded-2xl rounded-tr-sm bg-orange-500 px-2.5 py-1.5 text-[8px] text-white">Accepted ✅ See you Monday.</div>
    </div>
    <div className="mx-3 mb-3 flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5">
      <span className="flex-1 text-[8px] text-slate-500">Message…</span>
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500"><Send className="h-2.5 w-2.5 text-white" /></span>
    </div>
  </ScreenShell>
);

const DriverProfileScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex flex-col items-center px-4 pt-3">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-base font-bold text-white">MH</div>
      <p className="mt-2 text-[12px] font-bold">Marcus Hale</p>
      <div className="mt-0.5 flex items-center gap-1">
        {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-2.5 w-2.5 fill-orange-400 text-orange-400" />)}
        <span className="hsx-mono text-[8px] text-slate-300">4.9</span>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2 px-4 pt-3">
      <StatTile label="Gigs" value="142" />
      <StatTile label="Miles" value="86K" accent />
      <StatTile label="Years" value="11" />
    </div>
    <p className="mx-4 mt-3 text-[8px] uppercase tracking-wide text-slate-400">Credentials</p>
    <div className="mx-4 mt-1.5 space-y-1.5">
      {['CDL Class A — verified', 'FMCSA medical — current', 'Clean MVR — 5 yr', 'Passenger endorsement'].map((c) => (
        <div key={c} className="flex items-center gap-1.5 rounded-lg border border-emerald-400/15 bg-emerald-500/[0.06] px-2.5 py-1.5">
          <CheckCircle className="h-3 w-3 text-emerald-400" />
          <span className="text-[8px] text-slate-100">{c}</span>
        </div>
      ))}
    </div>
    <BottomNav active="profile" />
  </ScreenShell>
);

const GigDetailScreen = () => (
  <ScreenShell>
    <StatusBar />
    <div className="flex items-center gap-2 px-4 py-2">
      <ChevronLeft className="h-4 w-4 text-slate-400" />
      <p className="text-[11px] font-bold">Gig Details</p>
    </div>
    <div className="mx-4 h-20 overflow-hidden rounded-xl border border-white/10 bg-[#0c1322]">
      <div className="flex h-full items-center justify-center bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[length:14px_14px]">
        <MapPin className="h-5 w-5 text-orange-400" />
      </div>
    </div>
    <div className="mx-4 mt-2.5">
      <p className="text-[11px] font-bold">Nashville → Atlanta</p>
      <p className="mt-0.5 text-[8px] text-slate-400">Headliner tour · 248 mi · overnight</p>
    </div>
    <div className="grid grid-cols-2 gap-2 px-4 pt-2.5">
      <StatTile label="Pay rate" value="$450/day" accent />
      <StatTile label="Dates" value="Jun 12–18" />
    </div>
    <div className="mx-4 mt-3 space-y-1.5">
      {[{ k: 'Vehicle', v: '45ft entertainer coach' }, { k: 'Crew', v: '12 passengers' }, { k: 'Posted by', v: 'Acme Coachways ★ 4.8' }].map((x) => (
        <div key={x.k} className="flex items-center justify-between border-b border-white/5 pb-1.5">
          <span className="text-[8px] text-slate-400">{x.k}</span>
          <span className="text-[8px] font-medium text-slate-100">{x.v}</span>
        </div>
      ))}
    </div>
    <div className="mx-4 mt-auto mb-4">
      <button className="flex w-full items-center justify-center gap-1 rounded-full bg-orange-500 py-2 text-[9px] font-bold text-white">
        <Calendar className="h-3 w-3" /> Apply for this gig
      </button>
    </div>
  </ScreenShell>
);

/* ───────────────────────── Screen sets ───────────────────────── */
const SETS = {
  companies: {
    leftOuter: <RosterScreen />,
    leftInner: <ApplicantsScreen />,
    center: <CompanyHomeScreen />,
    rightInner: <MyGigsScreen />,
    rightOuter: <RevenueScreen />,
    subtitle: "Post gigs, review verified drivers, and track your bookings — the HopSeat experience from a bus company's view.",
  },
  drivers: {
    leftOuter: <DriverProfileScreen />,
    leftInner: <DriverBayScreen />,
    center: <DriverHomeScreen />,
    rightInner: <MessagesScreen />,
    rightOuter: <GigDetailScreen />,
    subtitle: 'Find matched gigs, apply in a tap, message companies, and trade in The Driver Bay — the HopSeat experience from a driver\u2019s seat.',
  },
};

/* ───────────────────────── Toggle ───────────────────────── */
const Toggle = ({ view, setView }) => (
  <div className="mx-auto mb-10 flex w-fit items-center gap-1 rounded-full border border-white/10 bg-black/40 p-1" data-testid="platform-preview-toggle">
    {[{ k: 'drivers', l: 'For Drivers' }, { k: 'companies', l: 'For Companies' }].map((o) => (
      <button
        key={o.k}
        onClick={() => setView(o.k)}
        data-testid={`platform-preview-toggle-${o.k === 'drivers' ? 'driver' : 'company'}`}
        className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
          view === o.k ? 'bg-orange-500 text-white shadow-[0_0_18px_rgba(249,115,22,0.4)]' : 'text-slate-400 hover:text-white'
        }`}
      >
        {o.l}
      </button>
    ))}
  </div>
);

/* ───────────────────────── Main ───────────────────────── */
const PlatformPreview = () => {
  const [view, setView] = useState('companies');
  const set = SETS[view];

  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-[#0a1018] py-16 md:py-20" data-testid="landing-platform-preview-section">
      <div className="hsx-grid-fade" />
      <div className="hsx-radial-glow" style={{ top: '14%', left: '50%', transform: 'translateX(-50%)' }} />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="mb-8 text-center">
          <div className="mb-3 flex items-center justify-center gap-2">
            <img src="/favicon-192x192.png?v=20260609" alt="HopSeat mark" className="h-6 w-6 object-contain" />
            <p className="hsx-mono text-xs uppercase tracking-[0.2em] text-orange-400">See it in action</p>
          </div>
          <h2 className="text-3xl font-bold text-white sm:text-4xl">Your whole operation, in your pocket</h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-slate-400" data-testid="platform-preview-subtitle">{set.subtitle}</p>
        </div>

        <Toggle view={view} setView={setView} />

        {/* 5-phone floating cluster */}
        <div key={view} className="hsx-screen-swap relative mx-auto flex h-[520px] w-full max-w-5xl items-center justify-center md:h-[560px]">
          <Phone
            testid="platform-preview-phone-left-outer"
            wrapClass="absolute left-1/2 hidden -translate-x-[182%] rotate-[-13deg] scale-[0.78] opacity-50 lg:block"
            floatClass="hsx-float-d" delay={1.2}
          >
            {set.leftOuter}
          </Phone>

          <Phone
            testid="platform-preview-phone-left-inner"
            wrapClass="absolute left-1/2 hidden -translate-x-[106%] rotate-[-8deg] scale-90 opacity-95 md:block z-10"
            floatClass="hsx-float-b" delay={0.6}
          >
            {set.leftInner}
          </Phone>

          <Phone
            testid="platform-preview-phone-center"
            wrapClass="relative z-30 scale-100 drop-shadow-2xl"
            floatClass="hsx-float-a" delay={0}
          >
            {set.center}
          </Phone>

          <Phone
            testid="platform-preview-phone-right-inner"
            wrapClass="absolute right-1/2 hidden translate-x-[106%] rotate-[8deg] scale-90 opacity-95 md:block z-10"
            floatClass="hsx-float-c" delay={0.9}
          >
            {set.rightInner}
          </Phone>

          <Phone
            testid="platform-preview-phone-right-outer"
            wrapClass="absolute right-1/2 hidden translate-x-[182%] rotate-[13deg] scale-[0.78] opacity-50 lg:block"
            floatClass="hsx-float-e" delay={1.5}
          >
            {set.rightOuter}
          </Phone>
        </div>
      </div>
    </section>
  );
};

export default PlatformPreview;
